/**
 * **Die Geheimnis-Prüfung** – Zugangsdaten in Notizen finden, bevor irgendetwas anderes mit dem
 * Text passiert ([Z-23](../../../docs/phasen/zusatz-23-notizbuch.md), „Zugangsdaten in Notizen").
 *
 * Die wichtigste Regel des Notizbuchs: Ein Passwort geht nie an ein Modell, nie in die Suche, nie
 * an Claude und steht nie im gespeicherten Text. Deshalb läuft diese Prüfung **ohne KI** – ein
 * Modell zu fragen, ob etwas ein Passwort ist, hieße, es ihm zu zeigen.
 *
 * Sie läuft an zwei Stellen mit demselben Code: im Browser (Hinweis beim Tippen) und auf dem
 * Server (vor dem Speichern, vor jedem Import). Deshalb nur Web-Standards, nichts aus `node:`.
 *
 * Lieber einmal zu viel maskiert als einmal zu wenig: Ein falscher Treffer kostet einen Klick
 * („kein Geheimnis"), ein übersehener ein Passwort beim KI-Anbieter.
 */

/** Woran ein Fund erkannt wurde – bestimmt die Anzeige in der Liste der Fundstellen. */
export const GEHEIMNIS_ARTEN = [
  'privater-schluessel',
  'token',
  'url-mit-zugang',
  'passwort',
  'zugang',
  'wlan',
  'zufall',
] as const
export type GeheimnisArt = (typeof GEHEIMNIS_ARTEN)[number]

export interface GeheimnisFund {
  art: GeheimnisArt
  /** Zeichenposition des Werts im Text, `ende` exklusiv. Nur der Wert, nie die Bezeichnung davor. */
  start: number
  ende: number
  /**
   * `sicher` bei eindeutigen Formen (Token-Vorsatz, `Passwort:`), `vielleicht` bei hoher
   * Zufälligkeit ohne Bezeichnung. Beide werden maskiert; die Liste zeigt den Unterschied.
   */
  sicherheit: 'sicher' | 'vielleicht'
}

/** Die Ersetzung im gespeicherten Text. Immer gleich lang – die Länge verrät sonst etwas. */
export const MASKE = '🔑••••••••'

/**
 * **„Im Klartext lassen"** (Nils, 23.10.2026: *„oder auch sagen können – im Klartext da lassen (bei
 * temporären Schlüsseln oder sowas)"*). Ein Wert mit `🔓` direkt davor ist bewusst stehen gelassen:
 * `geheimnisseFinden` meldet ihn nicht, gespeichert wird er wie geschrieben. An ein Modell geht er
 * trotzdem nie – `maskieren(text)` ohne Funde (so rufen es alle KI-Wege) maskiert ihn mit.
 */
export const OFFEN = '🔓'
const OFFEN_WERT = /🔓(?!•)[^\s🔑]+/gu

// ── Feste Formen ────────────────────────────────────────────────────────────────────────────

interface Form {
  art: GeheimnisArt
  muster: RegExp
}

/**
 * Bekannte Token-Formen. Die Gruppe `w` ist der Wert, der maskiert wird; fehlt sie, der ganze
 * Treffer. Reihenfolge egal – Überlappungen löst `geheimnisseFinden` auf.
 */
const FORMEN: Form[] = [
  {
    art: 'privater-schluessel',
    muster: /(?<w>-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z ]*PRIVATE KEY-----)/g,
  },
  // GitLab – alle Vorsätze (glpat-, gldt-, glrt-, glptt- …) und die neue Form mit Punkt-Teilen
  // „glpat-….01.171jg1d2h" (23.10.2026: der Rest hinter dem Punkt blieb im Text stehen)
  { art: 'token', muster: /(?<w>gl[a-z]{2,6}-[A-Za-z0-9_-]{20,}(?:\.[A-Za-z0-9_-]+)*)/g },
  { art: 'token', muster: /(?<w>gh[pousr]_[A-Za-z0-9]{30,})/g }, // GitHub
  { art: 'token', muster: /(?<w>github_pat_[A-Za-z0-9_]{30,})/g },
  { art: 'token', muster: /(?<w>sk-ant-[A-Za-z0-9_-]{20,})/g }, // Anthropic
  { art: 'token', muster: /(?<w>sk-(?:proj-|or-v1-)?[A-Za-z0-9_-]{20,})/g }, // OpenAI, OpenRouter
  { art: 'token', muster: /(?<w>AKIA[0-9A-Z]{16})/g }, // AWS
  // Laravel Sanctum („1|abc…", z. B. Coolify) – mit Nummer, sonst steht im Tresor nur die Hälfte
  { art: 'token', muster: /(?<![\w|])(?<w>\d+\|[A-Za-z0-9]{32,})/g },
  { art: 'token', muster: /(?<w>xox[abprs]-[A-Za-z0-9-]{10,})/g }, // Slack
  { art: 'token', muster: /(?<w>eyJ[A-Za-z0-9_-]{8,}\.eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,})/g }, // JWT
  { art: 'token', muster: /\bBearer\s+(?<w>[A-Za-z0-9._~+/=-]{16,})/g },
  // https://benutzer:passwort@host – nur das Passwort, der Rest ist eine normale Adresse
  { art: 'url-mit-zugang', muster: /\b[a-z][a-z0-9+.-]*:\/\/[^\s/:@]+:(?<w>[^\s/@]+)@/gi },
]

// ── Bezeichnete Werte ───────────────────────────────────────────────────────────────────────

/**
 * „Passwort: …", „PW = …", „Token …", „WLAN: Netz / Schlüssel", „Zugang ist lührs / geheim".
 *
 * Nach der Bezeichnung darf ein Doppelpunkt, ein Gleichheitszeichen oder „ist"/„lautet" stehen.
 * Bei `Benutzer / Passwort` (Schrägstrich mit Leerzeichen) ist nur der hintere Teil geheim.
 */
const BEZEICHNUNGEN: { art: GeheimnisArt; woerter: string }[] = [
  { art: 'passwort', woerter: 'passwort|passwörter|kennwort|password|passwd|pwd|pw|pass|pin' },
  {
    art: 'token',
    woerter:
      'token|api[- _]?key|api[- _]?schlüssel|apikey|secret|client[- _]?secret|zugangsschlüssel',
  },
  { art: 'wlan', woerter: 'wlan|wifi|wi-fi|wpa2?|netzwerkschlüssel' },
  { art: 'zugang', woerter: 'zugang|zugangsdaten|login|anmeldung|credentials' },
  // „🔑 Tresor: Fritzbox Büro" ist ein Eintragsname und bleibt; „Tresor: n((..asdjl" ist das
  // Passwort selbst (Nils hat es am 22.10. so geschrieben) – wie bei „Zugang:" entscheidet der Wert.
  { art: 'zugang', woerter: 'tresor' },
]

const TRENNER = String.raw`\s*(?:[:=]|\s(?:ist|lautet|is)\s)\s*`

function bezeichnungsMuster(woerter: string): RegExp {
  // (?<![\p{L}\d]) statt \b: \b kennt keine Umlaute („Passwörter").
  return new RegExp(
    String.raw`(?<![\p{L}\d])(?:${woerter})(?![\p{L}\d])` +
      TRENNER +
      String.raw`(?<w>\S+(?: \/ \S+)?)`,
    'giu',
  )
}

const BEZEICHNETE = BEZEICHNUNGEN.map((b) => ({
  art: b.art,
  muster: bezeichnungsMuster(b.woerter),
}))

/** Satzzeichen am Ende gehören nicht zum Passwort: „Passwort: geheim123." */
function satzendeAb(wert: string): string {
  return wert.replace(/[.,;)\]»“"']+$/u, '')
}

/**
 * Kein Geheimnis, sondern Inhalt. Nach „Passwort:", „PIN:", „Token:" gilt fast alles als geheim
 * – auch „1234" und „geheim". Nur nach „Zugang:"/„Login:" ist ein einzelnes Wort eher Text
 * („Login: über Microsoft", „Zugang: Admin").
 */
function istHarmlos(wert: string, art: GeheimnisArt): boolean {
  if (wert.length < 3) return true
  if (/^\d{1,2}\.\d{1,2}\.(\d{2}|\d{4})?$/.test(wert)) return true // Datum
  if (/^(?:x+|\*+|•+|\.{3}|…|\?+|-+|tbd|siehe|folgt|fehlt|offen|neu)$/iu.test(wert)) return true
  if (wert.startsWith('🔑')) return true // schon maskiert
  if (wert.startsWith(OFFEN)) return true // bewusst im Klartext
  if (art === 'zugang' && /^\p{L}+$/u.test(wert)) return true
  return false
}

// ── Zufällige Zeichenketten ohne Bezeichnung ────────────────────────────────────────────────

/** Bits je Zeichen (Shannon). Englischer Text liegt um 4, zufällige Schlüssel über 4,5. */
export function entropie(wert: string): number {
  const haeufigkeit = new Map<string, number>()
  for (const zeichen of wert) haeufigkeit.set(zeichen, (haeufigkeit.get(zeichen) ?? 0) + 1)
  let summe = 0
  for (const anzahl of haeufigkeit.values()) {
    const p = anzahl / wert.length
    summe -= p * Math.log2(p)
  }
  return summe
}

const KANDIDAT = /[A-Za-z0-9+/_=-]{24,}/g

/**
 * Eine lange Zeichenkette aus Groß-, Kleinbuchstaben und Ziffern mit hoher Zufälligkeit.
 * Ausgenommen, was oft vorkommt und nichts verrät: Prüfsummen (nur Hex), UUIDs, Teile von
 * Adressen und Dateipfaden.
 */
function istZufall(wert: string, davor: string): boolean {
  if (/^[0-9a-f]+$/i.test(wert)) return false // Hash, Commit
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(wert)) return false // UUID
  // ULID – Kennung einer Work-Aufgabe oder Notiz, auch als `?aufgabe=01KX…` (23.10.2026: die
  // Aufgaben-Links im Daily wurden maskiert)
  if (/^(?:[a-z_]+=)?[0-9A-HJKMNP-TV-Z]{26}$/.test(wert)) return false
  if (/[/.]$/.test(davor) || /:\/\/\S*$/.test(davor)) return false // mitten in einer Adresse
  const klassen = [/[a-z]/, /[A-Z]/, /\d/].filter((k) => k.test(wert)).length
  if (klassen < 3) return false
  return entropie(wert) >= 4.2
}

// ── Zusammen ────────────────────────────────────────────────────────────────────────────────

/**
 * Alle Geheimnisse in einem Text, nach Position sortiert, ohne Überlappungen. Bei Überlappung
 * gewinnt der frühere, bei gleichem Anfang der längere Fund.
 */
export function geheimnisseFinden(text: string): GeheimnisFund[] {
  const roh: GeheimnisFund[] = []

  for (const form of FORMEN) {
    for (const m of text.matchAll(form.muster)) {
      const wert = m.groups?.['w'] ?? m[0]
      // Schon maskiert („https://nutzer:🔑••••••••@host") – kein zweiter Fund (23.10.2026)
      if (wert === MASKE) continue
      const start = (m.index ?? 0) + m[0].indexOf(wert)
      roh.push({ art: form.art, start, ende: start + wert.length, sicherheit: 'sicher' })
    }
  }

  for (const b of BEZEICHNETE) {
    for (const m of text.matchAll(b.muster)) {
      let wert = satzendeAb(m.groups?.['w'] ?? '')
      let start = (m.index ?? 0) + m[0].lastIndexOf(m.groups?.['w'] ?? '')
      // „lührs / geheim": der Benutzername bleibt lesbar, das Passwort nicht.
      const paar = wert.indexOf(' / ')
      if (paar >= 0) {
        start += paar + 3
        wert = wert.slice(paar + 3)
      }
      if (istHarmlos(wert, b.art)) continue
      roh.push({ art: b.art, start, ende: start + wert.length, sicherheit: 'sicher' })
    }
  }

  for (const m of text.matchAll(KANDIDAT)) {
    const start = m.index ?? 0
    if (istZufall(m[0], text.slice(Math.max(0, start - 12), start))) {
      roh.push({ art: 'zufall', start, ende: start + m[0].length, sicherheit: 'vielleicht' })
    }
  }

  roh.sort((a, b) => a.start - b.start || b.ende - a.ende)
  const offeneBereiche = [...text.matchAll(OFFEN_WERT)].map(
    (m) => [m.index ?? 0, (m.index ?? 0) + m[0].length] as const,
  )
  const funde: GeheimnisFund[] = []
  for (const f of roh) {
    const letzter = funde.at(-1)
    if (letzter && f.start < letzter.ende) continue
    // im Klartext gelassen: alles vom 🔓 bis zum nächsten Leerraum („🔓1|abc…" bei Coolify)
    if (offeneBereiche.some(([a, e]) => f.start < e && f.ende > a)) continue
    funde.push(f)
  }
  return funde
}

/**
 * Der Text mit allen Funden ersetzt durch `MASKE`. Ohne Funde unverändert.
 *
 * Ohne `funde` (der Weg zu Modell, Suche, Protokoll) gehen auch die 🔓-Werte unter die Maske;
 * mit `funde` (Speichern) bleiben sie, wie Nils sie stehen gelassen hat.
 */
export function maskieren(text: string, funde?: readonly GeheimnisFund[]) {
  const auchOffene = funde === undefined
  let ergebnis = ''
  let bis = 0
  for (const f of funde ?? geheimnisseFinden(text)) {
    ergebnis += text.slice(bis, f.start) + MASKE
    bis = f.ende
  }
  ergebnis += text.slice(bis)
  return auchOffene
    ? ergebnis.replace(
        OFFEN_WERT,
        (w) => MASKE + w.slice(OFFEN.length + satzendeAb(w.slice(OFFEN.length)).length),
      )
    : ergebnis
}

/**
 * Ein Fingerabdruck des Werts – damit dasselbe Passwort in zwanzig Notizen **eine** Zeile in der
 * Liste der Fundstellen ist, ohne dass der Wert selbst irgendwo gespeichert wird. SHA-256, die
 * ersten 16 Hex-Zeichen; mit `salz` je Notizbuch, damit Fingerabdrücke zweier Notizbücher nichts
 * über gleiche Passwörter verraten.
 */
export async function fingerabdruck(wert: string, salz: string): Promise<string> {
  const daten = new TextEncoder().encode(salz + '\u0000' + wert)
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', daten))
  return [...hash.slice(0, 8)].map((b) => b.toString(16).padStart(2, '0')).join('')
}
